import { useState, useEffect, useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../../../app/store';
import {
  updateInsuranceAction as updateInsurance,
  removeInsuranceAction as removeInsurance
} from '../../../../../../services/EncounterService/slice';
import { useSearchParams } from 'react-router-dom';
import { useGetPayersQuery } from '../../../../../../services/ConfigurationService';
import type { InsuranceType } from '../../../../../../services/EncounterService/types/encounterTypes';
import type { PayerType } from '../../../../../../services/ConfigurationService/types/payerType';

// Helper function to create initial form data from insurance
const createInitialFormData = (insurance: InsuranceType | null): InsuranceType => {
  if (!insurance) {
    return {
      priority: 1,
      planId: '',
      planDescription: '',
      groupNumber: '',
      groupName: '',
      policyNumber: '',
      authNumber: '',
      authStartDate: undefined,
      authEndDate: undefined,
      insuranceStreet1: '',
      insuranceStreet2: '',
      insuranceCity: '',
      insuranceState: '',
      insuranceZip: '',
      insuranceCountry: '',
      payerCode: '',
      payerName: '',
      payerId: '',
      insuredsFirstName: '',
      insuredsMiddleName: '',
      insuredsLastName: '',
      insuredsSuffix: '',
      insuredsStreet1: '',
      insuredsStreet2: '',
      insuredsCity: '',
      insuredsState: '',
      insuredsZip: '',
      insuredsRelationship: '09',
      insuredsDateOfBirth: undefined,
    };
  }

  // Clean up empty string date fields to undefined for proper backend handling
  const cleanedInsurance = { ...insurance };
  if (cleanedInsurance.authStartDate === '') cleanedInsurance.authStartDate = undefined;
  if (cleanedInsurance.authEndDate === '') cleanedInsurance.authEndDate = undefined;
  if (cleanedInsurance.insuredsDateOfBirth === '') cleanedInsurance.insuredsDateOfBirth = undefined;

  return cleanedInsurance;
};

interface UseInsuranceFormReturn {
  formData: InsuranceType;
  selectedPayer: PayerType | null;
  sameAsPatient: boolean;
  error: string;
  payers: PayerType[];
  isEncounterCancelled: boolean;
  handleInputChange: (field: keyof InsuranceType, value: string | number) => void;
  handlePayerChange: (payer: PayerType | null) => void;
  handleSameAsPatientChange: (checked: boolean) => void;
  handleSubmit: () => void;
  handleDelete: () => void;
  handleClose: () => void;
  setError: (error: string) => void;
}

export const useInsuranceForm = (
  insurance: InsuranceType | null,
  open: boolean,
  onClose: () => void,
  onInsuranceUpdated?: () => void
): UseInsuranceFormReturn => {
  const dispatch = useAppDispatch();
  const encounter = useAppSelector(state => state.encounter.currentEncounter);
  const [searchParams] = useSearchParams();

  const selectedCenter = encounter?.caseIdentification?.centerId || '';
  const company = searchParams.get('company') || encounter?.caseIdentification?.company || '';
  const centerId = company && selectedCenter ? `${company}-${selectedCenter}` : '';

  // Check if encounter is cancelled
  const isEncounterCancelled = encounter?.status === 'CANCEL';

  // Form state
  const [formData, setFormData] = useState<InsuranceType>(createInitialFormData(insurance));
  const [selectedPayer, setSelectedPayer] = useState<PayerType | null>(null);
  const [error, setError] = useState('');
  const [sameAsPatient, setSameAsPatient] = useState(insurance?.sameAsPatient || false);

  // Fetch payers from configuration API
  const { data: payers = [] } = useGetPayersQuery(
    { company: company || '', centerId },
    { skip: !company || !centerId }
  );

  // Update form data when insurance prop changes
  useEffect(() => {
    if (open && insurance) {
      setFormData(createInitialFormData(insurance));
      setSameAsPatient(insurance.sameAsPatient || false);
      // Find and set the selected payer if it exists
      const currentPayer = payers.find(p => p.id === insurance.payerId || p.payerCode === insurance.payerCode);
      setSelectedPayer(currentPayer || null);
      setError('');
    }
  }, [open, insurance, payers]);

  const handleClose = useCallback(() => {
    onClose();
    setError('');
  }, [onClose]);

  const handleInputChange = useCallback((field: keyof InsuranceType, value: string | number) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  }, []);

  const handlePayerChange = useCallback((payer: PayerType | null) => {
    setSelectedPayer(payer);
    if (payer) {
      setFormData(prev => ({
        ...prev,
        payerCode: payer.payerCode,
        payerName: payer.payerName,
        payerId: payer.id
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        payerCode: '',
        payerName: '',
        payerId: ''
      }));
    }
  }, []);

  const handleSameAsPatientChange = useCallback((checked: boolean) => {
    setSameAsPatient(checked);
    if (checked && encounter?.patient) {
      const patient = encounter.patient;
      setFormData(prev => ({
        ...prev,
        insuredsFirstName: patient.firstName || '',
        insuredsMiddleName: patient.middleName || '',
        insuredsLastName: patient.lastName || '',
        insuredsSuffix: patient.suffix || '',
        insuredsStreet1: patient.street1 || '',
        insuredsStreet2: patient.street2 || '',
        insuredsCity: patient.city || '',
        insuredsState: patient.state || '',
        insuredsZip: patient.zip || '',
        insuredsRelationship: '01', // Self
        insuredsDateOfBirth: patient.birthDate || '',
      }));
    }
  }, [encounter?.patient]);

  const validateForm = useCallback((): boolean => {
    if (!formData.payerName?.trim()) {
      setError('Payer Name is required');
      return false;
    }

    if (!formData.policyNumber?.trim()) {
      setError('Policy Number is required');
      return false;
    }

    return true;
  }, [formData.payerName, formData.policyNumber]);

  const handleSubmit = useCallback(() => {
    if (!validateForm()) return;

    if (!insurance?.id) {
      setError('Insurance ID is required for updates');
      return;
    }

    // Clean up empty string date fields before sending to backend
    const cleanedFormData = { ...formData };
    if (cleanedFormData.authStartDate === '') cleanedFormData.authStartDate = undefined;
    if (cleanedFormData.authEndDate === '') cleanedFormData.authEndDate = undefined;
    if (cleanedFormData.insuredsDateOfBirth === '') cleanedFormData.insuredsDateOfBirth = undefined;

    // Include the sameAsPatient state
    const updatedData = { ...cleanedFormData, sameAsPatient };

    dispatch(updateInsurance({ id: insurance.id, updates: updatedData }));
    onInsuranceUpdated?.();
    handleClose();
  }, [validateForm, insurance?.id, formData, sameAsPatient, dispatch, onInsuranceUpdated, handleClose]);

  const handleDelete = useCallback(() => {
    if (!insurance?.id) return;

    dispatch(removeInsurance(insurance.id));
    handleClose();
  }, [insurance?.id, dispatch, handleClose]);

  return {
    formData,
    selectedPayer,
    sameAsPatient,
    error,
    payers,
    isEncounterCancelled,
    handleInputChange,
    handlePayerChange,
    handleSameAsPatientChange,
    handleSubmit,
    handleDelete,
    handleClose,
    setError
  };
};
